import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';

import * as Sentry from '@sentry/angular-ivy';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MainComponent } from '@modules/main/main.component';
import { LoginComponent } from '@/modules/login/login.component';
import { HeaderComponent } from '@modules/main/header/header.component';
import { FooterComponent } from '@modules/main/footer/footer.component';
import { MenuSidebarComponent } from '@modules/main/menu-sidebar/menu-sidebar.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RegisterComponent } from '@modules/register/register.component';
import { DashboardComponent } from '@pages/dashboard/dashboard.component';
import { ToastrModule } from 'ngx-toastr';
import { MessagesComponent } from '@modules/main/header/messages/messages.component';
import { NotificationsComponent } from '@modules/main/header/notifications/notifications.component';
import { ButtonComponent } from '@components/button/button.component';
import { ButtonDetailsComponent } from '@components/button/button-details/button-details.component';

import { registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import { UserComponent } from '@modules/main/header/user/user.component';
import { ForgotPasswordComponent } from '@modules/forgot-password/forgot-password.component';
import { RecoverPasswordComponent } from '@modules/recover-password/recover-password.component';
import { LanguageComponent } from '@modules/main/header/language/language.component';
import { PrivacyPolicyComponent } from '@modules/privacy-policy/privacy-policy.component';
import { MenuItemComponent } from '@components/menu-item/menu-item.component';
import { DropdownComponent } from '@components/dropdown/dropdown.component';
import { DropdownMenuComponent } from '@components/dropdown/dropdown-menu/dropdown-menu.component';
import { ControlSidebarComponent } from '@modules/main/control-sidebar/control-sidebar.component';
import { StoreModule } from '@ngrx/store';
import { authReducer } from './store/auth/reducer';
import { uiReducer } from './store/ui/reducer';
import { SelectComponent } from '@components/select/select.component';
import { CheckboxComponent } from '@components/checkbox/checkbox.component';
import { AgGridModule } from 'ag-grid-angular';
import { UsersComponent } from '@pages/admin/users/users.component';
import { UserService } from '@services/admin/user.service';
import { LoggerService } from '@services/logger.service';
import { Ng2SmartTableModule } from 'ng2-smart-table';
import { ButtonDetailUserComponent } from '@components/button/users/button-detail-user.component';
import { ButtonDeleteUserComponent } from '@components/button/users/button-delete-user.component';
import { ButtonEnabledUserComponent } from '@components/button/users/button-enabled-user.component';
import { UserDetailsComponent } from '@pages/admin/users/details/user-details.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DialogPopUpComponent } from '@components/dialog-pop-up/dialog-pop-up.component';
import { MatDialogModule } from '@angular/material/dialog';
import { DatePickerComponent } from '@components/date-picker/date-picker.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
  MatOptionModule,
} from '@angular/material/core';
import { DynamicFormComponent } from '@components/dynamic-form/dynamic-form.component';
import { TextInputComponent } from '@components/dynamic-form/text-input/text-input.component';
import { DropdownComponent as DropdownComponentForm } from './components/dynamic-form/dropdown/dropdown.component';
import { LabelComponent } from '@components/dynamic-form/label/label.component';
import { ControlComponent } from '@components/control/control.component';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { PasswordComponent } from '@components/dynamic-form/password/password.component';
import { TelephoneComponent } from '@components/dynamic-form/telephone/telephone.component';
import { EmailComponent } from '@components/dynamic-form/email/email.component';
import { UserFormDetailComponent } from '@pages/admin/users/details/user-form/user-form-detail.component';
import { AngularDualListBoxModule } from 'angular-dual-listbox';
import { DualListComponent } from '@components/dynamic-form/dual-list/dual-list.component';
import { NewUserComponent } from '@pages/admin/users/new-user/new-user.component';
import { AuthInterceptor } from '@services/auth-interceptor.service';
import { UserFormNewComponent } from '@pages/admin/users/new-user/user-form-new/user-form-new.component';
import { Registry } from '@/registry.service';
import { BoxErrorsComponent } from '@components/dynamic-form/box-errors/box-errors.component';
import { CompanyComponent } from '@pages/admin/company/company.component';
import { AlertComponent } from '@components/alert/alert.component';
import { PracticesComponent } from '@pages/practices-management/practices/practices.component';
import { NewPracticeFormComponent } from '@pages/practices-management/practices/new-practice/new-practice-form/new-practice-form.component';
import { NewPracticeComponent } from '@pages/practices-management/practices/new-practice/new-practice.component';
import { SlideToggleComponent } from '@components/dynamic-form/slide-toggle/slide-toggle.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DateInputComponent } from '@components/dynamic-form/date-input/date-input.component';
import { RepeaterInputsComponent } from '@components/dynamic-form/repeater-inputs/repeater-inputs.component';
import { NumberInputComponent } from '@components/dynamic-form/number-input/number-input.component';
import { TextAreaComponent } from '@components/dynamic-form/text-area/text-area.component';
import { AutoCompleteComponent } from '@components/dynamic-form/auto-complete/auto-complete.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { StockComponent } from '@pages/stock-management/stock.component';
import { UsedVehicleComponent } from '@pages/used-vehicle-management/used-vehicle.component';
import { SiNoRendererComponent } from '@/components/table-column-renderer/sino-renderer.component';
import { BrandRendererComponent } from '@components/table-column-renderer/brand-renderer.component';
import { StockAutorizzatoRendererComponent } from '@/components/table-column-renderer/stockautorizzato-renderer.component';
import { StatoUsatoRendererComponent } from '@/components/table-column-renderer/statousato-renderer.component';
import { HiddenInputComponent } from '@components/dynamic-form/hidden-input/hidden-input.component';
import { DetailPracticeFormComponent } from '@pages/practices-management/practices/practice-details/detail-practice-form/detail-practice-form.component';
import { PracticeDetailsComponent } from '@pages/practices-management/practices/practice-details/practice-details.component';
import { BrandComponent } from '@pages/tables/brand/brand.component';
import { InsuranceServiceTypeRendererComponent } from '@components/table-column-renderer/insurance-service-type-renderer.component';
import { ActionPracticesComponent } from '@pages/tables/action-practices/action-practices.component';
import { BigBuyersComponent } from '@pages/tables/big-buyers/big-buyers.component';
import { InsuranceServiceTypeComponent } from '@pages/tables/insurance-service-type/insurance-service-type.component';
import { RentalPracticesComponent } from '@pages/tables/rental-practices/rental-practices.component';
import { SellerComponent } from '@pages/tables/seller/seller.component';
import { ServicePracticesComponent } from '@pages/tables/service-practices/service-practices.component';
import { RentalPracticesRendererComponent } from '@components/table-column-renderer/rental-practices-renderer.component';
import { RentalAgencyComponent } from '@pages/tables/rental-agency/rental-agency.component';
import { CustomerTypeRendererComponent } from '@components/table-column-renderer/customer-type-renderer.component';
import { BrandNewRendererComponent } from '@components/table-column-renderer/brand-new-renderer.component';
import { EsclusioneGrandiAcquirentiRendererComponent } from '@components/table-column-renderer/esclusione-grandi-acquirenti-renderer.component';
import { AutoCompleteStringComponent } from '@components/dynamic-form/auto-complete/auto-complete-string/auto-complete-string.component';
import { AutoCompleteActionCodesComponent } from '@components/dynamic-form/auto-complete/concrete/auto-complete-action-codes/auto-complete-action-codes.component';
import { AutoCompleteLoyaltyServicesComponent } from '@components/dynamic-form/auto-complete/concrete/auto-complete-loyalty-services/auto-complete-loyalty-services.component';
import { AutoCompleteFinancialServicesComponent } from '@components/dynamic-form/auto-complete/concrete/auto-complete-financial-services/auto-complete-financial-services.component';
import { DialogNewExchangeComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-new-exchange/dialog-new-exchange.component';
import { DialogDetailExchangeComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-detail-exchange/dialog-detail-exchange.component';
import { ButtonDetailExchangeComponent } from '@components/button/practices/button-detail-exchange.component';
import { ButtonDeleteExchangeComponent } from '@components/button/practices/button-delete-exchange.component';
import { DialogUploadPracticeDocumentComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-upload-practice-document/dialog-upload-practice-document.component';
import { UploadPracticeDocumentFormComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-upload-practice-document/upload-practice-document-form.component';
import { UploadInputComponent } from '@components/dynamic-form/upload-input/upload-input.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SubagentAgencyComponent } from '@pages/tables/subagent-agency/subagent-agency.component';
import { ButtonDeleteDocumentComponent } from '@components/button/practices/button-delete-document.component';
import { ButtonDownloadDocumentComponent } from '@components/button/practices/button-download-document.component';
import { ButtonCreateNewSubAgentComponent } from '@components/button/tables/subagent-agency/button-create-new-sub-agent.component';
import { DialogSubAgentComponent } from '@pages/tables/subagent-agency/modals/dialog-sub-agent/dialog-sub-agent.component';
import { SubAgentFormComponent } from '@pages/tables/subagent-agency/modals/sub-agent-form.component';
import { ButtonModifySubAgentComponent } from '@components/button/tables/subagent-agency/button-modify-sub-agent.component';
import { ButtonDetailSubAgentComponent } from '@components/button/practices/button-detail-subagent.component';
import { ButtonDeleteSubAgentComponent } from '@components/button/tables/subagent-agency/button-delete-subagent.component';
import { VehicleAccessoryComponent } from '@pages/tables/vehicle-accessory/vehicle-accessory.component';
import { ButtonDeleteActionPracticeComponent } from '@components/button/tables/action-practices/button-delete-action-practice.component';
import { ButtonDetailActionPracticeComponent } from '@components/button/tables/action-practices/button-modify-action-practice.component';
import { DialogActionPracticeComponent } from '@pages/tables/action-practices/dialog-action-practice/dialog-action-practice.component';
import { ActionPracticeFormComponent } from '@pages/tables/action-practices/dialog-action-practice/form/action-practice-form.component';
import { ButtonNewActionPracticeComponent } from '@components/button/tables/action-practices/button-new-action-practice.component';
import { DialogServicePracticeComponent } from '@pages/tables/service-practices/dialog-service-practice/dialog-service-practice.component';
import { ServicePracticeFormComponent } from '@pages/tables/service-practices/dialog-service-practice/form/service-practice-form.component';
import { ButtonDeleteServicePracticeComponent } from '@components/button/tables/service-practices/button-delete-service-practice.component';
import { ButtonDetailServicePracticeComponent } from '@components/button/tables/service-practices/button-modify-service-practice.component';
import { ButtonNewServicePracticeComponent } from '@components/button/tables/service-practices/button-new-service-practice.component';
import { AutoCompleteTypeInsuranceServiceComponent } from '@components/dynamic-form/auto-complete/concrete/auto-complete-type-insurance-service/auto-complete-type-insurance-service.component';
import { DialogBrandComponent } from '@pages/tables/brand/dialog-brand/dialog-brand.component';
import { BrandFormComponent } from '@pages/tables/brand/dialog-brand/form/brand-form.component';
import { ButtonDeleteBrandComponent } from '@components/button/tables/brands/button-delete-brand.component';
import { ButtonDetailBrandComponent } from '@components/button/tables/brands/button-modify-brand.component';
import { ButtonNewBrandComponent } from '@components/button/tables/brands/button-new-brand.component';
import { DialogSellerComponent } from '@pages/tables/seller/dialog-seller/dialog-seller.component';
import { SellerFormComponent } from '@pages/tables/seller/dialog-seller/form/seller-form.component';
import { ButtonDeleteSellerComponent } from '@components/button/tables/seller/button-delete-seller.component';
import { ButtonNewSellerComponent } from '@components/button/tables/seller/button-new-seller.component';
import { ButtonDetailSellerComponent } from '@components/button/tables/seller/button-modify-seller.component';
import { SellerRendererComponent } from '@components/table-column-renderer/seller-renderer.component';
import { DialogBigBuyersComponent } from '@pages/tables/big-buyers/dialog-big-buyers/dialog-big-buyers.component';
import { BigBuyersFormComponent } from '@pages/tables/big-buyers/dialog-big-buyers/form/big-buyers-form.component';
import { ButtonDeleteBigBuyersComponent } from '@components/button/tables/big-buyers/button-delete-big-buyer.component';
import { ButtonNewBigBuyersComponent } from '@components/button/tables/big-buyers/button-new-big-buyer.component';
import { ButtonDetailBigBuyersComponent } from '@components/button/tables/big-buyers/button-modify-big-buyer.component';
import { DialogRentalAgencyComponent } from '@pages/tables/rental-agency/dialog-rental-agency/dialog-rental-agency.component';
import { ButtonDeleteRentalAgencyComponent } from '@components/button/tables/rental-agency/button-delete-rental-agency.component';
import { ButtonNewRentalAgencyComponent } from '@components/button/tables/rental-agency/button-new-rental-agency.component';
import { ButtonDetailRentalAgencyComponent } from '@components/button/tables/rental-agency/button-modify-rental-agency.component';
import { RentalAgencyFormComponent } from '@pages/tables/rental-agency/dialog-rental-agency/form/rental-agency-form.component';
import { DialogVehicleAccessoryComponent } from '@pages/tables/vehicle-accessory/dialog-vehicle-accessory/dialog-vehicle-accessory.component';
import { VehicleAccessoryFormComponent } from '@pages/tables/vehicle-accessory/dialog-vehicle-accessory/form/vehicle-accessory-form.component';
import { ButtonDeleteVehicleAccessoryComponent } from '@components/button/tables/vehicle-accessory/button-delete-vehicle-accessory.component';
import { ButtonNewVehicleAccessoryComponent } from '@components/button/tables/vehicle-accessory/button-new-vehicle-accessory.component';
import { ButtonDetailVehicleAccessoryComponent } from '@components/button/tables/vehicle-accessory/button-modify-vehicle-accessory.component';
import { PercentageInputComponent } from '@components/dynamic-form/percentage-input/percentage-input.component';
import { DialogInsuranceServiceTypeComponent } from '@pages/tables/insurance-service-type/dialog-insurance-service-type/dialog-insurance-service-type.component';
import { InsuranceServiceTypeFormComponent } from '@pages/tables/insurance-service-type/dialog-insurance-service-type/form/insurance-service-type-form.component';
import { ButtonDeleteInsuranceServiceTypeComponent } from '@components/button/tables/insurance-service-type/button-delete-insurance-service-type.component';
import { ButtonDetailInsuranceServiceTypeComponent } from '@components/button/tables/insurance-service-type/button-modify-insurance-service-type.component';
import { ButtonNewInsuranceServiceTypeComponent } from '@components/button/tables/insurance-service-type/button-new-insurance-service-type.component';
import { DialogRentalPracticesComponent } from '@pages/tables/rental-practices/dialog-rental-practices/dialog-rental-practices.component';
import { RentalPracticesFormComponent } from '@pages/tables/rental-practices/dialog-rental-practices/form/rental-practices-form.component';
import { StatusBarComponent } from '@components/status-bar/status-bar.component';
import { ButtonNewRentalPracticeComponent } from '@components/button/tables/rental-practices/button-new-rental-practice.component';
import { ButtonDetailRentalPracticeComponent } from '@components/button/tables/rental-practices/button-modify-rental-practice.component';
import { ButtonDeleteRentalPracticeComponent } from '@components/button/tables/rental-practices/button-delete-rental-practice.component';
import { ModelBrandRendererComponent } from '@components/table-column-renderer/model-brand-renderer.component';
import { AutoCompleteModelComponent } from '@components/dynamic-form/auto-complete/concrete/auto-complete-model/auto-complete-model.component';
import { ModelsComponent } from '@pages/tables/models/models.component';
import { DialogModelsComponent } from '@pages/tables/models/dialog-models/dialog-models.component';
import { ModelsFormComponent } from '@pages/tables/models/dialog-models/form/models-form.component';
import { ButtonNewModelsComponent } from '@components/button/tables/models/button-new-models.component';
import { ButtonDeleteModelsComponent } from '@components/button/tables/models/button-delete-models.component';
import { ButtonDetailModelsComponent } from '@components/button/tables/models/button-modify-models.component';
import { AutoCompleteBrandComponent } from '@components/dynamic-form/auto-complete/concrete/auto-complete-brand/auto-complete-brand.component';
import { DialogNewAccessoryPracticeComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-new-accessory/dialog-new-accessory-practice.component';
import { AccessoryPracticeFormComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-new-accessory/form/accessory-practice-form.component';
import { ButtonDeletePracticeAccessoryComponent } from '@components/button/practices/button-delete-practice-accessory.component';
import { ButtonDetailPracticeAccessoryComponent } from '@components/button/practices/button-detail-practice-accessory.component';
import { TableHeaderComponent } from '@components/table-header/table-header.component';
import { DialogDuplicatePracticeComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-duplicate-practice/dialog-duplicate-practice.component';
import { DuplicatePracticeFormComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-duplicate-practice/form/duplicate-practice-form.component';
import { ExchangeFormComponent } from '@pages/practices-management/practices/practice-details/dialogs/exchange-form.component';
import { CurrencyInputDirective } from '@/directives/currency-input.directive';
import { SubagentAgencyRendererComponent } from '@components/table-column-renderer/subagent-agency-renderer.component';
import { AutoCompleteSubagentAgencyComponent } from '@components/dynamic-form/auto-complete/concrete/auto-complete-subagent-agency/auto-complete-subagent-agency.component';
import { NumberInputPipe } from './pipes/number-input.pipe';
import { CashMovementsComponent } from '@pages/cash-management/cash-movements/cash-movements.component';
import { MenuBiConfigComponent } from '@pages/tables/menu-bi-config/menu-bi-config.component';
import { MenuBiComponent } from '@pages/menu-bi/menu-bi.component';
import { ButtonOpenLinkMenuBiComponent } from '@components/button/menu-bi/button-open-link-menu-bi.component';
import { SubagentAgencyUsersRendererComponent } from '@components/table-column-renderer/subagent-agency-users-renderer.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DialogLoadingComponent } from '@components/dialog-loading/dialog-loading.component';
import { ButtonNewPracticeComponent } from '@components/button/practices/button-new-practice.component';
import { ButtonNewUserComponent } from '@components/button/users/button-new-user.component';
import { NewTableComponent } from '@components/new-table/new-table.component';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { DecimalIntPipe } from './pipes/decimal-int.pipe';
import { ButtonDeleteComponent } from '@components/button/button-delete/button-delete.component';
import { ColumnsNamePipe } from './pipes/columns-name.pipe';
import { ButtonNewMenuBiComponent } from '@components/button/tables/menu-bi-config/button-new-menu-bi.component';
import { DialogMenuBiConfigComponent } from '@pages/tables/menu-bi-config/dialog-menu-biconfig/dialog-menu-bi-config.component';
import { MenuBiConfigFormComponent } from '@pages/tables/menu-bi-config/dialog-menu-biconfig/form/menu-bi-config-form.component';
import { ButtonDeleteMenuBIComponent } from '@components/button/tables/menu-bi-config/button-delete-menu-bi.component';
import { ButtonDetailMenuBIComponent } from '@components/button/tables/menu-bi-config/button-modify-menu-bi.component';
import { AreaBiRendererComponent } from '@components/table-column-renderer/area-bi-renderer.component';
import { MakeDropdownMsComponent } from '@components/new-dropdown/make-dropdown-ms.component';
import { ControlValueAccessorDirective } from './directives/control-value-accessor.directive';
import { DialogNewCommunicationPracticeComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-new-communication/dialog-new-communication-practice.component';
import { ComunicationPracticeFormComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-new-communication/form/comunication-practice-form.component';
import { ButtonDeletePracticeCommunicationComponent } from '@components/button/practices/button-delete-practice-communication.component';
import { ButtonDetailPracticeCommunicationComponent } from '@components/button/practices/button-detail-practice-communication.component';
import { DialogDetailCommunicationPracticeComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-detail-communication/dialog-detail-communication-practice.component';
import { DialogDetailAccessoryPracticeComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-detail-accessory/dialog-detail-accessory-practice.component';
import { ButtonNewCashMovementsComponent } from '@components/button/cash-movements/button-new-cash-movements.component';
import { DeliveryCardboardManagementComponent } from '@pages/practices-management/delivery-cardboard-management/delivery-cardboard-management.component';
import { DialogDeliveryCardboardComponent } from '@pages/practices-management/delivery-cardboard-management/dialog-delivery-cardboard/dialog-delivery-cardboard.component';
import { DeliveryCardboardFormComponent } from '@pages/practices-management/delivery-cardboard-management/dialog-delivery-cardboard/form/delivery-cardboard-form.component';
import { NewCashMovementsComponent } from '@pages/cash-management/cash-movements/new-cash-movements/new-cash-movements.component';
import { NewCashMovementsFormComponent } from '@pages/cash-management/cash-movements/new-cash-movements/form/new-cash-movements-form.component';
import { InlineControlComponent } from '@components/inline-control/inline-control.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ParseCheckboxPipe } from './parse-checkbox.pipe';
import { AutoCompletePaymentTypeComponent } from '@components/dynamic-form/auto-complete/concrete/auto-complete-payment-type/auto-complete-payment-type.component';
import { AutoCompleteContactSourceComponent } from '@components/dynamic-form/auto-complete/concrete/auto-complete-contact-source/auto-complete-contact-source.component';
import { AutoCompleteWarrantyComponent } from '@components/dynamic-form/auto-complete/concrete/auto-complete-warranty/auto-complete-warranty.component';
import { WarrantyFormComponent } from '@pages/tables/warranty/dialog-warranty/form/warranty-form.component';
import { DialogWarrantyComponent } from '@pages/tables/warranty/dialog-warranty/dialog-warranty.component';
import { WarrantyComponent } from '@pages/tables/warranty/warranty.component';
import { PaymentTypeFormComponent } from '@pages/tables/payment-type/dialog-payment-type/form/payment-type-form.component';
import { DialogPaymentTypeComponent } from '@pages/tables/payment-type/dialog-payment-type/dialog-payment-type.component';
import { PaymentTypeComponent } from '@pages/tables/payment-type/payment-type.component';
import { ContactSourceFormComponent } from '@pages/tables/contact-source/dialog-contact-source/form/contact-source-form.component';
import { DialogContactSourceComponent } from '@pages/tables/contact-source/dialog-contact-source/dialog-contact-source.component';
import { ContactSourceComponent } from '@pages/tables/contact-source/contact-source.component';
import { ButtonNewContactSourceComponent } from '@components/button/tables/contact-source/button-new-contact-source.component';
import { ButtonDeleteContactSourceComponent } from '@components/button/tables/contact-source/button-delete-contact-source.component';
import { ButtonDetailContactSourceComponent } from '@components/button/tables/contact-source/button-modify-contact-source.component';
import { ButtonNewWarrantyComponent } from '@components/button/tables/warranty/button-new-warranty.component';
import { ButtonDeleteWarrantyComponent } from '@components/button/tables/warranty/button-delete-warranty.component';
import { ButtonDetailWarrantyComponent } from '@components/button/tables/warranty/button-modify-warranty.component';
import { ButtonNewPaymentTypeComponent } from '@components/button/tables/payment-type/button-new-payment-type.component';
import { ButtonDetailPaymentTypeComponent } from '@components/button/tables/payment-type/button-modify-payment-type.component';
import { ButtonDeletePaymentTypeComponent } from '@components/button/tables/payment-type/button-delete-payment-type.component';
import { BrandGoalsComponent } from '@pages/practices-management/brand-goals/brand-goals.component';
import { DialogBrandGoalsComponent } from '@pages/practices-management/brand-goals/dialog-brand-goals/dialog-brand-goals.component';
import { BrandGoalsFormComponent } from '@pages/practices-management/brand-goals/dialog-brand-goals/form/brand-goals-form.component';
import { ButtonDetailBrandGoalsComponent } from '@components/button/brand-goals/button-modify-brand-goals.component';
import { ButtonDownloadExchangeComponent } from '@components/button/practices/button-download-exchange.component';
import { TableDeliveryCartonComponent } from '@pages/practices-management/delivery-cardboard-management/table-delivery-carton/table-delivery-carton.component';
import { Router } from '@angular/router';
import { UsedVehicleDetailComponent } from '@pages/used-vehicle-management/details/used-vehicle-detail.component';
import { UsedVehicleFormComponent } from '@pages/used-vehicle-management/form/used-vehicle-form.component';
import { BusinessMachinesComponent } from '@pages/practices-management/business-machines/business-machines.component';
import { SellersUsersRendererComponent } from '@components/table-column-renderer/sellers-users-renderer.component';
import { BigbuyerBrandGoalsRendererComponent } from '@components/table-column-renderer/bigbuyer-brand-goals-renderer.component';
import { DetailPracticeVehicleFormComponent } from '@pages/practices-management/practices/practice-details/detail-practice-vehicle-form/detail-practice-vehicle-form.component';
import { CurrencyInputComponent } from '@components/dynamic-form/currency-input/currency-input.component';
import { CURRENCY_MASK_CONFIG, CurrencyMaskConfig, CurrencyMaskModule } from 'ng2-currency-mask';
import { DataVehicleNotMatchedFormComponent } from '@pages/practices-management/practices/practice-details/data-vehicle-not-matched-form/data-vehicle-not-matched-form.component';
import { BackButtonComponent } from '@components/button/back-button/back-button.component';
import { SlideToggleUserComponent } from '@components/slide-toggle-user/slide-toggle-user.component';
import { ButtonDeleteBankAccountComponent } from '@components/button/tables/bank-account/button-delete-bank-account.component';
import { ButtonDetailBankAccountComponent } from '@components/button/tables/bank-account/button-modify-bank-account.component';
import { ButtonNewBankAccountComponent } from '@components/button/tables/bank-account/button-new-bank-account.component';
import { BankAccountComponent } from '@pages/tables/bank-account/bank-account.component';
import { BankAccountFormComponent } from '@pages/tables/bank-account/dialog-bank-account/form/bank-account-form.component';
import { DialogBankAccountComponent } from '@pages/tables/bank-account/dialog-bank-account/dialog-bank-account.component';
import { ButtonRecalculateProgressionMonthComponent } from '@components/button/practices/button-recalculate-progression-month.component';
import { ButtonExportExcelDeliveryCardBoardComponent } from '@components/button/practices/button-export-excel-delivery-card-board.component';
import { AutoCompleteBankPaymentComponent } from './components/dynamic-form/auto-complete/concrete/auto-complete-bank-payment/auto-complete-bank-payment.component';
import { BiggerDiscountComponent } from './pages/bigger-discount/bigger-discount.component';
import { FormUploadCircularComponent } from './pages/bigger-discount/form-upload-circular/form-upload-circular.component';
import { TableBiggerDiscountComponent } from './pages/bigger-discount/table-bigger-discount/table-bigger-discount.component';
import { ImportSummaryComponent } from '@pages/imports/import-summary/import-summary.component';
import { CrossF7TableComponent } from '@pages/imports/cross-f7-table/cross-f7-table.component';
import { CrossF8TableComponent } from '@pages/imports/cross-f8-table/cross-f8-table.component';
import { VwfsDashboardTableComponent } from '@pages/imports/vwfs-dashboard-table/vwfs-dashboard-table.component';
import { VwfsCommissionTableComponent } from '@pages/imports/vwfs-commission-table/vwfs-commission-table.component';
import { VwfsFrameTableComponent } from '@pages/imports/vwfs-frame-table/vwfs-frame-table.component';
import { AccountingTabComponentComponent } from '@pages/practices-management/practices/practice-details/accounting-tab-component/accounting-tab-component.component';
import { MsAzpTabComponentComponent } from '@pages/practices-management/practices/practice-details/ms-azp-tab-component/ms-azp-tab-component.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { DynamicTableComponent } from '@components/dynamic-table-component/dynamic-table.component';
import { AccountingPracticeFormComponent } from '@pages/practices-management/practices/practice-details/accounting-tab-component/accounting-forms/accounting-practice-form.component';
import { LoyaltyTableComponent } from '@pages/imports/loyalty-table/loyalty-table.component';
import { VwbankCommissionTableComponent } from '@pages/imports/vwbank-commission-table/vwbank-commission-table.component';
import { TableMSComponent } from '@pages/bigger-discount/table-ms/table-ms.component';
import { DocumentationMmssComponent } from '@pages/mmss-management/documentation-mmss/documentation-mmss.component';
import { DialogMSUpdateComponent } from '@pages/bigger-discount/table-ms/dialog-msupdate/dialog-msupdate.component';
import { MSUpdatesFormComponent } from '@pages/bigger-discount/table-ms/dialog-msupdate/form/ms-updates-form.component';
import { YearDatePickerInputComponent } from '@components/dynamic-form/year-date-picker-input/year-date-picker-input.component';
import { MonthDatePickerInputComponent } from '@components/dynamic-form/month-date-picker-input/month-date-picker-input.component';
import { NewButtonMSDiscountComponent } from '@components/button/ms-discounts/table-ms/new-button-m-s-discount.component';
import { SellerTypesRendererComponent } from '@components/table-column-renderer/seller-types-renderer.component';
import { DialogUploadMultipleDocumentComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-upload-multiple-document/dialog-upload-multiple-document.component';
import { DocumentMultipleFormComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-upload-multiple-document/document-multiple-form.component';
import { ProgressBarComponent } from '@components/progress-bar/progress-bar.component';
import { PracticeDeletedComponent } from '@pages/imports/practice-deleted/practice-deleted.component';
import { FinancialDataAnalysisComponent } from '@pages/practices-management/practices/practice-details/financial-data-analysis/financial-data-analysis.component';
import { RentalPracticeListComponent } from '@pages/rental-management/rental-practice-list.component';
import { RentalTimelineComponent } from '@pages/rental-management/rental-timeline/rental-timeline.component';
import { CashMovementsAnalisisComponent } from '@pages/cash-management/cash-movements-analisis/cash-movements-analisis.component';
import { RentPracticeDetailComponent } from '@pages/rental-management/rent-practice-detail/rent-practice-detail.component';
import { RentPracticeDetailFormComponent } from '@pages/rental-management/rent-practice-detail/rent-practice-detail-form/rent-practice-detail-form.component';
import { CustomDateAdapter } from '@/custom-date-adapter';
import { TableGroupRowsComponent } from '@components/table-group-rows/table-group-rows.component';
import { FormCreateRentablePracticeComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-rentable-practice-new/form/form-create-rentable-practice.component';
import { DialogRentablePracticeNewComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-rentable-practice-new/dialog-rentable-practice-new.component';
import { DialogNewUsedVehicleComponent } from '@pages/used-vehicle-management/dialog-new-used-vehicle/dialog-new-used-vehicle.component';
import { ButtonNewUsedVehicleComponent } from '@components/button/used-vehicles/button-new-used-vehicle-component';
import { SalesInvoiceFormComponent } from '@pages/practices-management/practices/practice-details/accounting-tab-component/accounting-forms/sales-invoice-form.component';
import { RentalMovementsListComponent } from '@pages/rental-management/rent-practice-detail/rental-movements-list/rental-movements-list.component';
import { DialogFormRentalMovementComponent } from '@pages/rental-management/rent-practice-detail/rental-movements-list/dialog-form-rental-movement/dialog-form-rental-movement.component';
import { RentalMovementFormComponent } from '@pages/rental-management/rent-practice-detail/rental-movements-list/form/rental-movement-form.component';
import { RentalInsuranceListComponent } from '@pages/rental-management/rent-practice-detail/rental-insurance-list/rental-insurance-list.component';
import { RentalInsuranceFormComponent } from '@pages/rental-management/rent-practice-detail/rental-insurance-list/dialog-form-rental-insurance/form/rental-insurance-form.component';
import { DialogFormRentalInsuranceComponent } from '@pages/rental-management/rent-practice-detail/rental-insurance-list/dialog-form-rental-insurance/dialog-form-rental-insurance.component';
import { CommissionComponent } from '@pages/tables/commission/commission.component';
import { ButtonNewCommissionComponent } from '@components/button/tables/commissions/button-new-commission.component';
import { ButtonDetailCommissionComponent } from '@components/button/tables/commissions/button-modify-commission.component';
import { ButtonDeleteCommissionComponent } from '@components/button/tables/commissions/button-delete-commission.component';
import { DialogCommissionComponent } from '@pages/tables/commission/dialog-commission/dialog-commission.component';
import { CommissionFormComponent } from '@pages/tables/commission/dialog-commission/form/commission-form.component';
import { DialogSelectClientRentComponent } from '@pages/rental-management/rent-practice-detail/rental-movements-list/dialog-form-rental-movement/dialog-select-client-rent/dialog-select-client-rent.component';
import { DialogMsDuplicateComponent } from '@pages/bigger-discount/table-ms/dialog-msduplicate/dialog-ms-duplicate.component';
import { DialogUploadDocumentComponent } from '@pages/mmss-management/documentation-mmss/dialog-upload-document/dialog-upload-document.component';
import { FormUploadMmssDocumentComponent } from '@pages/mmss-management/documentation-mmss/dialog-upload-document/form-upload-document/form-upload-document.component';
import { MsDuplicateFormComponent } from '@pages/bigger-discount/table-ms/dialog-msduplicate/form/ms-duplicate-form.component';
import { RentMovementActiveFormComponent } from '@pages/rental-management/rent-practice-detail/rental-movement-active-form/rent-movement-active-form.component';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MatMomentDateModule,
} from '@angular/material-moment-adapter';
import { GLOBAL_DATE_FORMATS } from '@/date-picker-formats';
import { YearMonthDatePickerInputComponent } from '@components/dynamic-form/year-month-date-picker-input/year-month-date-picker-input.component';
import { DialogSelectCompanyRentComponent } from '@pages/rental-management/rent-practice-detail/rental-movements-list/dialog-form-rental-movement/dialog-select-company-rent/dialog-select-company-rent.component';
import { DialogRenewInsuranceComponent } from '@pages/rental-management/rent-practice-detail/rental-insurance-list/dialog-renew-insurance/dialog-renew-insurance.component';
import { RentalRenewInsuranceFormComponent } from '@pages/rental-management/rent-practice-detail/rental-insurance-list/dialog-renew-insurance/form/rental-renew-insurance-form.component';
import { VhmReportTableComponent } from '@pages/imports/vhm-report-table/vhm-report-table.component';
import { VhmProFormaTableComponent } from '@pages/imports/vhm-pro-forma-table/vhm-pro-forma-table.component';
import { OrderSliTableComponent } from '@pages/imports/order-sli-table/order-sli-table.component';
import { DialogFormReturnRentalMovementComponentComponent } from '@pages/rental-management/rent-practice-detail/dialog-form-return-rental-movement-component/dialog-form-return-rental-movement-component.component';
import { RentalReturnMovementFormComponent } from '@pages/rental-management/rent-practice-detail/dialog-form-return-rental-movement-component/form/rental-return-movement-form.component';
import { RentalReservationListComponent } from '@pages/rental-management/rental-reservation-list/rental-reservation-list.component';
import { RentalReservationsListComponent } from '@pages/rental-management/rent-practice-detail/rental-reservations-list/rental-reservations-list.component';
import { DialogFormRentalReservationComponent } from '@pages/rental-management/rental-reservation-list/dialog-form-rental-reservation/dialog-form-rental-reservation.component';
import { RentalReservationFormComponent } from '@pages/rental-management/rental-reservation-list/dialog-form-rental-reservation/form/rental-reservation-form.component';
import { DialogLinkPracticeComponent } from '@pages/rental-management/rental-reservation-list/dialog-form-rental-reservation/dialog-link-practice/dialog-link-practice.component';
import { ReservationConflictBannerComponent } from '@pages/rental-management/rental-reservation-list/dialog-form-rental-reservation/reservation-conflict-banner/reservation-conflict-banner.component';
import { DateTimeInputComponent } from '@components/dynamic-form/date-time-input/date-time-input.component';
import {
  NgxMatDatetimePickerModule,
  NgxMatNativeDateModule,
} from '@angular-material-components/datetime-picker';
import { DialogMigratePracticeComponent } from '@pages/practices-management/practices/practice-details/dialogs/dialog-migrate-practice-component/dialog-migrate-practice.component';
import { MatCardModule } from '@angular/material/card';
import { InsurancesPageListComponent } from '@pages/insurances/insurances-page-list.component';
import { InsuranceDetailComponent } from '@pages/insurances/insurance-detail/insurance-detail.component';
import { InsuranceFormComponent } from '@pages/insurances/form/insurance-form.component';
import { DialogLinkInsuranceToVehicleComponent } from '@pages/insurances/insurance-detail/dialog-link-insurance-to-vehicle/dialog-link-insurance-to-vehicle.component';
import { FormAssociateInsuranceToVehicleComponent } from '@pages/insurances/insurance-detail/dialog-link-insurance-to-vehicle/form-associate-insurance-to-vehicle/form-associate-insurance-to-vehicle.component';
import { DialogNewInsuranceComponent } from '@pages/insurances/dialog-new-insurance/dialog-new-insurance.component';
import { DialogCreateCashFoundComponent } from '@pages/cash-management/cash-movements-analisis/dialog-create-cash-found/dialog-create-cash-found.component';
import { CreateCashFoundFormComponent } from '@pages/cash-management/cash-movements-analisis/dialog-create-cash-found/form/create-cash-found-form.component';
import { DialogDeleteCashFundsComponent } from '@pages/cash-management/cash-movements-analisis/dialog-delete-cash-funds/dialog-delete-cash-funds.component';
import { FormUploadInsuranceFileComponent } from '@pages/insurances/insurance-detail/dialog-upload-insurance-document/form-upload-file-insurance/form-upload-insurance-file.component';
import { DialogUploadInsuranceDocumentComponent } from '@pages/insurances/insurance-detail/dialog-upload-insurance-document/dialog-upload-insurance-document.component';
import { AutoCompleteRentalPracticeComponent } from '@components/dynamic-form/auto-complete/concrete/auto-complete-rental-practice/auto-complete-model.component';
import { DialogModifyAssociationComponent } from '@pages/insurances/insurance-detail/dialog-modify-association/dialog-modify-association.component';
import { FormModifyAssociationComponent } from '@pages/insurances/insurance-detail/dialog-modify-association/form-modify-association-insurance/form-modify-association-insurance.component';
import { DialogMsCopyRowComponent } from '@pages/bigger-discount/table-ms/dialog-ms-copy-row/dialog-ms-copy-row.component';
import { MsCopyRowFormComponent } from '@pages/bigger-discount/table-ms/dialog-ms-copy-row/form/ms-copy-row-form.component';
import { DetailPracticeUtilizzatoreFormComponent } from '@pages/practices-management/practices/practice-details/detail-practice-utilizzatore-form/detail-practice-utilizzatore-form.component';
import { DetailPracticeUsedCostsFormComponent } from '@pages/practices-management/practices/practice-details/detail-practice-used-costs-form/detail-practice-used-costs-form.component';
import { VehicleRegistryPageListComponent } from '@pages/vehicle-registry/vehicle-registry-page-list.component';
import { GaragePassagesComponent } from '@pages/garage-passages/garage-passages.component';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { EmailConfirmationDialogComponent } from '@components/email-confirmation-dialog/email-confirmation-dialog.component';
import { GaragePassageDetailDialogComponent } from '@pages/garage-passages/garage-passage-detail-dialog/garage-passage-detail-dialog.component';
import { GaragePassageDetailFormComponent } from '@pages/garage-passages/garage-passage-detail-form/garage-passage-detail-form.component';
import { DialogExtractDataComponent } from '@pages/garage-passages/dialog-extract-data/dialog-extract-data.component';
import { SellersUserRendererComponent } from '@components/table-column-renderer/sellers-user-renderer.component';
import { CustomersComponent } from '@pages/tables/customers/customers.component';
import { TestDrivePageListComponent } from '@pages/test-drive/test-drive-page-list.component';
import { TestDriveDetailComponent } from '@pages/test-drive/test-drive-detail/test-drive-detail.component';
import { TestDriveFormComponent } from '@pages/test-drive/test-drive-detail/test-drive-form/test-drive-form.component';
import { AutoCompleteVehicleComponent } from '@components/dynamic-form/auto-complete/concrete/auto-complete-vehicle/auto-complete-vehicle.component';
import { AutoCompleteClienteComponent } from '@components/dynamic-form/auto-complete/concrete/auto-complete-cliente/auto-complete-cliente.component';
import { AutoCompleteUtilizzatoreComponent } from '@components/dynamic-form/auto-complete/concrete/auto-complete-utilizzatore/auto-complete-utilizzatore.component';
import { GrandiAcquirentiListComponent } from '@components/dynamic-form/grandi-acquirenti-list/grandi-acquirenti-list.component';
import { CommissionSignalmanComponent } from '@pages/tables/commission-signalman/commission-signalman.component';
import { ButtonDetailCommissionSignalmanComponent } from '@components/button/tables/commissions-signalman/button-modify-commission-signalman.component';
import { ButtonNewCommissionSignalmanComponent } from '@components/button/tables/commissions-signalman/button-new-commission-signalman.component';
import { ButtonDeleteCommissionSignalmanComponent } from '@components/button/tables/commissions-signalman/button-delete-commission-signalman.component';
import { DialogCommissionSignalmanComponent } from '@pages/tables/commission-signalman/dialog-commission/dialog-commission-signalman.component';
import { CommissionSignalmanFormComponent } from '@pages/tables/commission-signalman/dialog-commission/form/commission-signalman-form.component';

registerLocaleData(localeIt, 'it-IT');

export let InjectorInstance: Injector;
export const CustomCurrencyMaskConfig: CurrencyMaskConfig = {
  align: 'left',
  allowNegative: false,
  decimal: ',',
  precision: 2,
  prefix: '',
  suffix: ' €',
  thousands: '.',
};

@NgModule({
  declarations: [
    AppComponent,
    MainComponent,
    LoginComponent,
    HeaderComponent,
    FooterComponent,
    MenuSidebarComponent,
    RegisterComponent,
    DashboardComponent,
    MessagesComponent,
    NotificationsComponent,
    ButtonComponent,
    UserComponent,
    ForgotPasswordComponent,
    RecoverPasswordComponent,
    LanguageComponent,
    PrivacyPolicyComponent,
    MenuItemComponent,
    DropdownComponent,
    DropdownMenuComponent,
    ControlSidebarComponent,
    SelectComponent,
    ProgressBarComponent,
    CheckboxComponent,
    UsersComponent,
    ButtonDetailUserComponent,
    ButtonDeleteUserComponent,
    ButtonEnabledUserComponent,
    ButtonNewUserComponent,
    ButtonNewUsedVehicleComponent,
    UserDetailsComponent,
    DialogPopUpComponent,
    DatePickerComponent,
    DynamicFormComponent,
    TextInputComponent,
    DropdownComponentForm,
    LabelComponent,
    ControlComponent,
    PasswordComponent,
    TelephoneComponent,
    EmailComponent,
    UserFormDetailComponent,
    DualListComponent,
    NewUserComponent,
    UserFormNewComponent,
    BoxErrorsComponent,
    CompanyComponent,
    AlertComponent,
    SiNoRendererComponent,
    BrandRendererComponent,
    BrandNewRendererComponent,
    StockAutorizzatoRendererComponent,
    StatoUsatoRendererComponent,
    EsclusioneGrandiAcquirentiRendererComponent,
    SubagentAgencyUsersRendererComponent,
    PracticesComponent,
    ButtonDetailSubAgentComponent,
    ButtonDeleteSubAgentComponent,
    ButtonDeleteActionPracticeComponent,
    ButtonDetailActionPracticeComponent,
    ButtonNewActionPracticeComponent,
    ButtonDetailExchangeComponent,
    ButtonDeleteDocumentComponent,
    ButtonDownloadDocumentComponent,
    ButtonDeleteExchangeComponent,
    NewPracticeComponent,
    NewPracticeFormComponent,
    SlideToggleComponent,
    DateInputComponent,
    DateTimeInputComponent,
    YearDatePickerInputComponent,
    YearMonthDatePickerInputComponent,
    MonthDatePickerInputComponent,
    RepeaterInputsComponent,
    NumberInputComponent,
    TextAreaComponent,
    AutoCompleteComponent,
    StockComponent,
    UsedVehicleComponent,
    HiddenInputComponent,
    PracticeDetailsComponent,
    DetailPracticeFormComponent,
    DeliveryCardboardFormComponent,
    PracticeDetailsComponent,
    BrandComponent,
    InsuranceServiceTypeRendererComponent,
    ActionPracticesComponent,
    BigBuyersComponent,
    InsuranceServiceTypeComponent,
    RentalPracticesComponent,
    SellerComponent,
    ServicePracticesComponent,
    RentalPracticesRendererComponent,
    RentalAgencyComponent,
    CustomerTypeRendererComponent,
    AutoCompleteStringComponent,
    AutoCompleteActionCodesComponent,
    AutoCompleteLoyaltyServicesComponent,
    AutoCompleteFinancialServicesComponent,
    DialogNewExchangeComponent,
    DialogMigratePracticeComponent,
    ExchangeFormComponent,
    DialogDetailExchangeComponent,
    DialogUploadPracticeDocumentComponent,
    DialogUploadMultipleDocumentComponent,
    UploadPracticeDocumentFormComponent,
    DocumentMultipleFormComponent,
    UploadInputComponent,
    SubagentAgencyComponent,
    ButtonCreateNewSubAgentComponent,
    DialogSubAgentComponent,
    SubAgentFormComponent,
    ButtonModifySubAgentComponent,
    VehicleAccessoryComponent,
    DialogActionPracticeComponent,
    ActionPracticeFormComponent,
    DialogServicePracticeComponent,
    ServicePracticeFormComponent,
    ButtonDeleteServicePracticeComponent,
    ButtonDetailServicePracticeComponent,
    ButtonNewServicePracticeComponent,
    AutoCompleteTypeInsuranceServiceComponent,
    DialogBrandComponent,
    BrandFormComponent,
    ButtonDeleteBrandComponent,
    ButtonDetailBrandComponent,
    ButtonNewBrandComponent,
    ButtonDetailsComponent,
    DialogSellerComponent,
    SellerFormComponent,
    ButtonDeleteSellerComponent,
    ButtonNewSellerComponent,
    ButtonDetailSellerComponent,
    SellerRendererComponent,
    SellerTypesRendererComponent,
    DialogBigBuyersComponent,
    BigBuyersFormComponent,
    ButtonDeleteBigBuyersComponent,
    ButtonDetailBigBuyersComponent,
    ButtonNewBigBuyersComponent,
    DialogRentalAgencyComponent,
    ButtonDeleteRentalAgencyComponent,
    ButtonNewRentalAgencyComponent,
    ButtonDetailRentalAgencyComponent,
    RentalAgencyFormComponent,
    DialogVehicleAccessoryComponent,
    VehicleAccessoryFormComponent,
    ButtonDeleteVehicleAccessoryComponent,
    ButtonNewVehicleAccessoryComponent,
    ButtonDetailVehicleAccessoryComponent,
    PercentageInputComponent,
    DialogInsuranceServiceTypeComponent,
    InsuranceServiceTypeFormComponent,
    ButtonDeleteInsuranceServiceTypeComponent,
    ButtonDetailInsuranceServiceTypeComponent,
    ButtonNewInsuranceServiceTypeComponent,
    DialogRentalPracticesComponent,
    RentalPracticesFormComponent,
    StatusBarComponent,
    ButtonDeleteRentalPracticeComponent,
    ButtonNewRentalPracticeComponent,
    ButtonDetailRentalPracticeComponent,
    ModelBrandRendererComponent,
    AutoCompleteModelComponent,
    AutoCompleteRentalPracticeComponent,
    ModelsComponent,
    DialogModelsComponent,
    ModelsFormComponent,
    ButtonNewModelsComponent,
    ButtonDeleteModelsComponent,
    ButtonDetailModelsComponent,
    AutoCompleteBrandComponent,
    DialogNewAccessoryPracticeComponent,
    AccessoryPracticeFormComponent,
    ButtonDeletePracticeAccessoryComponent,
    ButtonDetailPracticeAccessoryComponent,
    TableHeaderComponent,
    AccountingPracticeFormComponent,
    DialogDuplicatePracticeComponent,
    DuplicatePracticeFormComponent,
    CurrencyInputDirective,
    SubagentAgencyRendererComponent,
    AutoCompleteSubagentAgencyComponent,
    NumberInputPipe,
    CashMovementsComponent,
    MenuBiConfigComponent,
    MenuBiComponent,
    ButtonOpenLinkMenuBiComponent,
    DialogLoadingComponent,
    ButtonNewPracticeComponent,
    NewTableComponent,
    DecimalIntPipe,
    ButtonDeleteComponent,
    ColumnsNamePipe,
    ButtonNewMenuBiComponent,
    DialogMenuBiConfigComponent,
    MenuBiConfigFormComponent,
    ButtonDeleteMenuBIComponent,
    ButtonDetailMenuBIComponent,
    AreaBiRendererComponent,
    MakeDropdownMsComponent,
    ControlValueAccessorDirective,
    DialogNewCommunicationPracticeComponent,
    DialogDetailCommunicationPracticeComponent,
    ComunicationPracticeFormComponent,
    ButtonDeletePracticeCommunicationComponent,
    ButtonDetailPracticeCommunicationComponent,
    DialogDetailAccessoryPracticeComponent,
    ButtonNewCashMovementsComponent,
    DeliveryCardboardManagementComponent,
    DialogDeliveryCardboardComponent,
    NewCashMovementsComponent,
    NewCashMovementsFormComponent,
    InlineControlComponent,
    ParseCheckboxPipe,
    AutoCompletePaymentTypeComponent,
    AutoCompleteContactSourceComponent,
    AutoCompleteWarrantyComponent,
    WarrantyFormComponent,
    DialogWarrantyComponent,
    WarrantyComponent,
    PaymentTypeFormComponent,
    DialogPaymentTypeComponent,
    PaymentTypeComponent,
    ContactSourceFormComponent,
    DialogContactSourceComponent,
    ContactSourceComponent,
    ButtonNewContactSourceComponent,
    ButtonDeleteContactSourceComponent,
    ButtonDetailContactSourceComponent,
    CustomersComponent,
    ButtonNewWarrantyComponent,
    ButtonDeleteWarrantyComponent,
    ButtonDetailWarrantyComponent,
    ButtonNewPaymentTypeComponent,
    ButtonDetailPaymentTypeComponent,
    ButtonDeletePaymentTypeComponent,
    BrandGoalsComponent,
    DialogBrandGoalsComponent,
    DialogMSUpdateComponent,
    DialogMsDuplicateComponent,
    DialogUploadDocumentComponent,
    FormUploadMmssDocumentComponent,
    BrandGoalsFormComponent,
    ButtonDetailBrandGoalsComponent,
    ButtonDownloadExchangeComponent,
    TableDeliveryCartonComponent,
    UsedVehicleDetailComponent,
    UsedVehicleFormComponent,
    BusinessMachinesComponent,
    PracticeDeletedComponent,
    SellersUsersRendererComponent,
    BigbuyerBrandGoalsRendererComponent,
    DetailPracticeVehicleFormComponent,
    CurrencyInputComponent,
    DataVehicleNotMatchedFormComponent,
    BackButtonComponent,
    SlideToggleUserComponent,
    BankAccountComponent,
    ButtonDeleteBankAccountComponent,
    ButtonDetailBankAccountComponent,
    ButtonNewBankAccountComponent,
    BankAccountFormComponent,
    DialogBankAccountComponent,
    ButtonRecalculateProgressionMonthComponent,
    ButtonExportExcelDeliveryCardBoardComponent,
    AutoCompleteBankPaymentComponent,
    BiggerDiscountComponent,
    FormUploadCircularComponent,
    TableBiggerDiscountComponent,
    ImportSummaryComponent,
    CrossF7TableComponent,
    CrossF8TableComponent,
    VwfsDashboardTableComponent,
    VwfsCommissionTableComponent,
    VwfsFrameTableComponent,
    AccountingTabComponentComponent,
    MsAzpTabComponentComponent,
    DynamicTableComponent,
    LoyaltyTableComponent,
    VwbankCommissionTableComponent,
    TableMSComponent,
    DocumentationMmssComponent,
    NewButtonMSDiscountComponent,
    MSUpdatesFormComponent,
    FinancialDataAnalysisComponent,
    CashMovementsAnalisisComponent,
    RentalPracticeListComponent,
    RentalTimelineComponent,
    RentalMovementsListComponent,
    RentalInsuranceListComponent,
    RentPracticeDetailComponent,
    RentPracticeDetailFormComponent,
    RentMovementActiveFormComponent,
    TableGroupRowsComponent,
    FormCreateRentablePracticeComponent,
    DialogRentablePracticeNewComponent,
    DialogNewUsedVehicleComponent,
    DialogFormRentalMovementComponent,
    DialogFormReturnRentalMovementComponentComponent,
    DialogSelectClientRentComponent,
    DialogSelectCompanyRentComponent,
    DialogFormRentalInsuranceComponent,
    DialogRenewInsuranceComponent,
    SalesInvoiceFormComponent,
    RentalMovementFormComponent,
    RentalReturnMovementFormComponent,
    RentalInsuranceFormComponent,
    RentalRenewInsuranceFormComponent,
    RentalReservationListComponent,
    RentalReservationsListComponent,
    DialogFormRentalReservationComponent,
    RentalReservationFormComponent,
    DialogLinkPracticeComponent,
    ReservationConflictBannerComponent,
    CommissionComponent,
    ButtonNewCommissionComponent,
    ButtonDetailCommissionComponent,
    ButtonDeleteCommissionComponent,
    DialogCommissionComponent,
    CommissionSignalmanComponent,
    ButtonNewCommissionSignalmanComponent,
    ButtonDetailCommissionSignalmanComponent,
    ButtonDeleteCommissionSignalmanComponent,
    DialogCommissionSignalmanComponent,
    CommissionSignalmanFormComponent,
    CommissionFormComponent,
    MsDuplicateFormComponent,
    VhmReportTableComponent,
    VhmProFormaTableComponent,
    OrderSliTableComponent,
    InsurancesPageListComponent,
    InsuranceDetailComponent,
    InsuranceFormComponent,
    FormAssociateInsuranceToVehicleComponent,
    DialogLinkInsuranceToVehicleComponent,
    DialogModifyAssociationComponent,
    DialogNewInsuranceComponent,
    DialogCreateCashFoundComponent,
    CreateCashFoundFormComponent,
    DialogDeleteCashFundsComponent,
    FormUploadInsuranceFileComponent,
    DialogUploadInsuranceDocumentComponent,
    FormModifyAssociationComponent,
    DialogMsCopyRowComponent,
    MsCopyRowFormComponent,
    DetailPracticeUtilizzatoreFormComponent,
    DetailPracticeUsedCostsFormComponent,
    VehicleRegistryPageListComponent,
    GaragePassagesComponent,
    EmailConfirmationDialogComponent,
    GaragePassageDetailDialogComponent,
    GaragePassageDetailFormComponent,
    DialogExtractDataComponent,
    SellersUserRendererComponent,
    TestDrivePageListComponent,
    TestDriveDetailComponent,
    TestDriveFormComponent,
    AutoCompleteVehicleComponent,
    AutoCompleteClienteComponent,
    AutoCompleteUtilizzatoreComponent,
    GrandiAcquirentiListComponent,
  ],
  imports: [
    BrowserModule,
    StoreModule.forRoot({ auth: authReducer, ui: uiReducer }),
    HttpClientModule,
    AppRoutingModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    AgGridModule,
    ToastrModule.forRoot({
      timeOut: 3000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
    }),
    Ng2SmartTableModule,
    MatTabsModule,
    MatIconModule,
    MatDialogModule,
    FontAwesomeModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatInputModule,
    MatNativeDateModule,
    FormsModule,
    MatButtonModule,
    MatOptionModule,
    MatSelectModule,
    AngularDualListBoxModule,
    MatSlideToggleModule,
    MatAutocompleteModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCheckboxModule,
    CurrencyMaskModule,
    MatExpansionModule,
    MatMomentDateModule,
    NgxMatDatetimePickerModule,
    NgxMatNativeDateModule,
    MatCardModule,
    MatSnackBarModule,
  ],
  providers: [
    { provide: CURRENCY_MASK_CONFIG, useValue: CustomCurrencyMaskConfig },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    UserService,
    LoggerService,
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: GLOBAL_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'it-IT' },
    Registry,
  ],
  bootstrap: [AppComponent],
  exports: [AccountingPracticeFormComponent, FormCreateRentablePracticeComponent],
})
export class AppModule {
  constructor(private injector: Injector) {
    InjectorInstance = this.injector;
  }
}
